import React, { useState, useMemo } from "react";
import {
  Rocket,
  ChevronUp,
  ChevronDown,
  ChevronExpand,
  ArrowClockwise,
  ExclamationTriangle,
} from "react-bootstrap-icons";

import styles from "./Classperformanceheatmap.module.css";
import { useSelector } from "react-redux";

/* ============================================================================
 * Shared constants
 * ==========================================================================*/
/* Default scheme — exam marks (existing behavior preserved) */
const EXAM_MARK_BUCKETS = {
  strong: {
    label: "Strong (7 - 10)",
    swatchClass: "swatchStrong",
    min: 71, max: 100,
  },
  average: {
    label: "Average (5 - 6.9)",
    swatchClass: "swatchAverage",
    min: 50, max: 70,
  },
  needsAttention: {
    label: "Needs Attention (0 - 4.9)",
    swatchClass: "swatchNeedsAttention",
    min: 0, max: 49,
  },
};

/* Syllabus completion scheme — only fully-covered (>=100%) is "good". */
export const SYLLABUS_MARK_BUCKETS = {
  strong: {
    label: "Completed (>=100%)",
    swatchClass: "swatchStrong",
    min: 100, max: Number.MAX_SAFE_INTEGER,
  },
  needsAttention: {
    label: "Needs Attention (<100%)",
    swatchClass: "swatchNeedsAttention",
    min: 0, max: 99,
  },
};

/* Pick the bucket whose [min, max] range contains the mark. */
const resolveBucket = (mark, buckets) => {
  for (const [key, b] of Object.entries(buckets)) {
    if (mark >= b.min && mark <= b.max) return key;
  }
  return Object.keys(buckets)[0];   // fallback to first bucket
};

const getMarkBucket = (mark) => {
  if (mark >= 71) return "strong";
  if (mark >= 50) return "average";
  return "needsAttention";
};

const DEFAULT_PERIOD_OPTIONS = [
  { value: "quarterly", label: "Quarterly" },
  { value: "half_yearly", label: "Half Yearly" },
  { value: "annual", label: "Annual" },
];

/* Default options for the (All / Middle / Secondary / Higher Secondary) filter */
const DEFAULT_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "middle", label: "Middle" },
  { value: "secondary", label: "Secondary" },
  { value: "higher_secondary", label: "Higher Secondary" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/* ============================================================================
 * REUSABLE: MarkCell — colored swatch + numeric value (+ optional sub-label)
 *   Exported so parents can use it inside their column `render` functions.
 *
 *   tooltipLines: optional string[] — when provided, hovering the swatch/value
 *   shows a small dark tooltip (one line per entry). Positioned `fixed` to the
 *   cursor so it is never clipped by the scrollable table container.
 * ==========================================================================*/

const SWATCH_CLASS = {
  strong: styles.swatchStrong,
  average: styles.swatchAverage,
  needsAttention: styles.swatchNeedsAttention,
};
export const MarkCell = ({
  mark,
  subLabel,
  buckets = EXAM_MARK_BUCKETS,
  tooltipLines = null,
}) => {
  const [tipPos, setTipPos] = useState(null); // {x, y} while hovering, else null

  if (mark === null || mark === undefined) {
    return <span className={styles.markEmpty}>—</span>;
  }
  const bucketKey = resolveBucket(mark, buckets);
  const bucket = buckets[bucketKey];

  const hasTip = Array.isArray(tooltipLines) && tooltipLines.length > 0;
  const track = (e) => setTipPos({ x: e.clientX, y: e.clientY });
  const clear = () => setTipPos(null);

  return (
    <div className={styles.markCell} aria-label={`${mark} marks, ${bucket.label}`}>
      <div
        style={{ display: "flex", alignItems: "center", gap: 8 }}
        onMouseEnter={hasTip ? track : undefined}
        onMouseMove={hasTip ? track : undefined}
        onMouseLeave={hasTip ? clear : undefined}
      >
        <span className={`${styles.markSwatch} ${styles[bucket.swatchClass]}`} />
        <span className={styles.markValue}>{mark}</span>
      </div>
      {subLabel ? <span className={styles.markSubLabel}>{subLabel}</span> : null}

      {hasTip && tipPos && (
        <div
          style={{
            position: "fixed",
            left: tipPos.x + 14,
            top: tipPos.y + 14,
            zIndex: 2000,
            background: "rgba(17, 24, 39, 0.95)",
            color: "#fff",
            padding: "8px 10px",
            borderRadius: 8,
            fontSize: 12,
            lineHeight: 1.5,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            boxShadow: "0 6px 18px rgba(0, 0, 0, 0.25)",
          }}
        >
          {tooltipLines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ============================================================================
 * REUSABLE: SummaryPill
 * ==========================================================================*/
export const SummaryPill = ({ label, value, variant = "pass" }) => (
  <div
    className={`${styles.summaryPill} ${variant === "pass" ? styles.summaryPillPass : styles.summaryPillFail
      }`}
  >
    <div className={styles.summaryLabel}>{label}</div>
    <div className={styles.summaryValue}>{value}%</div>
  </div>
);

/* ============================================================================
 * REUSABLE: PerformanceLegend
 * ==========================================================================*/
export const PerformanceLegend = ({ buckets = EXAM_MARK_BUCKETS }) => (
  <div className={styles.legend}>
    {Object.entries(buckets).map(([key, b]) => (
      <div key={key} className={styles.legendItem}>
        <span className={`${styles.legendSwatch} ${styles[b.swatchClass]}`} />
        <span>{b.label}</span>
      </div>
    ))}
  </div>
);

/* ============================================================================
 * REUSABLE: Pagination
 * ==========================================================================*/
export const Pagination = ({ page, perPage, total, hasPrev, hasNext, onPageChange }) => {
  const start = total === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);

  return (
    <div className={styles.paginationRow}>
      <div className={styles.entriesText}>
        Showing {start} to {end} of {total} entries
      </div>
      <div className={styles.paginationBtns}>
        <button
          type="button"
          className={styles.pageBtn}
          disabled={!hasPrev}
          onClick={() => onPageChange?.(page - 1)}
        >
          Previous
        </button>
        <button
          type="button"
          className={styles.pageBtn}
          disabled={!hasNext}
          onClick={() => onPageChange?.(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
};

/* ============================================================================
 * REUSABLE: SortIcon
 * ==========================================================================*/
const SortIcon = ({ direction }) => {
  if (direction === "asc") return <ChevronUp size={12} className={styles.sortIcon} />;
  if (direction === "desc") return <ChevronDown size={12} className={styles.sortIcon} />;
  return <ChevronExpand size={12} className={`${styles.sortIcon} ${styles.sortIconIdle}`} />;
};

/* ============================================================================
 * REUSABLE: HeatmapTable — fully generic, column-driven by the PARENT.
 *
 *   columns -> [{
 *     key,                 // unique id; also default cell accessor (row[key])
 *     label,               // header text
 *     isName,              // bool — applies the sticky/name cell styling
 *     sortable,            // bool (default true) — show sort control
 *     sortAccessor,        // (row) => comparable value (default row[key])
 *     render,              // (row) => node (default row[key])
 *   }]
 *   rows    -> array of row objects (parent's mapped data)
 *
 *  The component knows nothing about subjects/total/rank — the parent decides.
 * ==========================================================================*/
export const HeatmapTable = ({ columns = [], rows = [] }) => {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const colByKey = useMemo(
    () => Object.fromEntries(columns.map((c) => [c.key, c])),
    [columns],
  );

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const valueOf = (col, row) =>
    col?.sortAccessor ? col.sortAccessor(row) : row?.[col.key];

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const col = colByKey[sortKey];
    if (!col) return rows;
    return [...rows].sort((a, b) => {
      let av = valueOf(col, a);
      let bv = valueOf(col, b);
      if (typeof av === "string" || typeof bv === "string") {
        av = String(av ?? "").toLowerCase();
        bv = String(bv ?? "").toLowerCase();
      } else {
        av = av ?? 0;
        bv = bv ?? 0;
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, sortKey, sortDir, colByKey]);

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`${styles.th} ${col.isName ? styles.thName : ""}`}
              >
                {col.sortable === false ? (
                  <span>{col.label}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSort(col.key)}
                    className={styles.sortBtn}
                  >
                    <span>{col.label}</span>
                    <SortIcon direction={sortKey === col.key ? sortDir : null} />
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sortedRows.map((row, i) => (
            <tr key={row.id ?? i} className={i % 2 === 1 ? styles.rowAlt : styles.row}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`${styles.td} ${col.isName ? styles.tdName : ""}`}
                >
                  {col.render ? col.render(row) : row?.[col.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ============================================================================
 * State sub-components
 * ==========================================================================*/
const SkeletonState = () => (
  <div className={styles.skeletonBody}>
    <div className={styles.skeletonSummary}>
      <div className={styles.skeletonPill} />
      <div className={styles.skeletonPill} />
    </div>
    {[...Array(6)].map((_, i) => (
      <div key={i} className={styles.skeletonRow} />
    ))}
  </div>
);

const ErrorState = ({ onRetry }) => (
  <div className={styles.errorState}>
    <ExclamationTriangle size={40} className={styles.errorIcon} />
    <p className={styles.errorText}>Something went wrong while loading the data.</p>
    {onRetry && (
      <button type="button" onClick={onRetry} className={styles.retryBtn}>
        <ArrowClockwise size={16} /> Retry
      </button>
    )}
  </div>
);

const EmptyState = () => (
  <div className={styles.emptyState}>No data available.</div>
);

/* ============================================================================
 * PARENT: ClassPerformanceHeatmap (presentational shell)
 *
 *   columns  -> column definitions supplied by the PARENT (see HeatmapTable)
 *   data     -> { summary?, rows | students, pagination }
 *   status   -> "idle" | "loading" | "success" | "error"
 *
 *   Toolbar / pagination / period / filter / summary / legend toggles are
 *   unchanged. The table body is now whatever `columns` describe.
 * ==========================================================================*/
const ClassPerformanceHeatmap = ({
  columns = [],
  data,
  title,
  status = "loading",
  page = 1,
  perPage = 10,
  period = "",
  periodOptions = [],
  search = "",
  searchPlaceholder = "",
  searchEnable = true,
  onPageChange,
  onLimitChange,
  onSearchChange,
  onPeriodChange,
  onRetry,
  legendBuckets,
  // show / hide toggles
  showPeriod = true,
  showSummary = true,
  showLegend = true,
  // filter tabs
  showFilter = false,
  filterOptions = DEFAULT_FILTER_OPTIONS,
  filterValue = "all",
  onFilterChange,
}) => {
  const rows = data?.rows ?? data?.students ?? [];
  const hasColumns = Array.isArray(columns) && columns.length > 0;

  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations?.labels]);

  return (
    <div className={styles.card}>
      {/* ---- Header ---- */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Rocket size={20} className={styles.headerIcon} />
          <h2 className={styles.title}>  {title}</h2>
        </div>

        <div className={styles.headerRight}>
          {showFilter && (
            <div className={styles.tabs}>
              {filterOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`${styles.tab} ${filterValue === opt.value ? styles.tabActive : ""}`}
                  onClick={() => onFilterChange?.(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {showPeriod && (
            <select
              value={period}
              onChange={(e) => onPeriodChange?.(e.target.value)}
              className={styles.periodSelect}
              aria-label="Select period"
              disabled={!periodOptions || periodOptions.length === 0}
            >
              {(!periodOptions || periodOptions.length === 0) ? (
                <option value="">No exam available</option>
              ) : (
                periodOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))
              )}
            </select>
          )}
        </div>
      </div>

      {(status === "idle" || status === "loading") && <SkeletonState />}
      {status === "error" && <ErrorState onRetry={onRetry} />}
      {status === "success" && (!data || !hasColumns) && <EmptyState />}

      {/* Success path — render shell once we have a column definition */}
      {status === "success" && data && hasColumns && (
        <>
          {/* ---- Summary pills ---- */}
          {showSummary && data.summary && (
            <div className={styles.summaryRow}>
              <SummaryPill
                label="Class Pass Percentage"
                value={data.summary.passPercentage}
                variant="pass"
              />
              <SummaryPill
                label="Class Fail Percentage"
                value={data.summary.failPercentage}
                variant="fail"
              />
            </div>
          )}

          {/* ---- Toolbar (page size + search) ---- */}
          <div className={styles.toolbar}>
            <select
              value={perPage}
              onChange={(e) => onLimitChange?.(Number(e.target.value))}
              className={styles.limitSelect}
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            {searchEnable && (
              <input
                type="text"
                value={search}
                onChange={(e) => onSearchChange?.(e.target.value)}
                placeholder={searchPlaceholder}
                className={styles.searchInput}
                aria-label="Search rows"
              />
            )}

          </div>

          {/* ---- Table OR empty row ---- */}
          {rows.length > 0 ? (
            <HeatmapTable columns={columns} rows={rows} />
          ) : (
            <div className={styles.tableEmpty}>
              {search?.trim()
                ? `No results match "${search.trim()}".`
                : "No Exam available."}
            </div>
          )}

          {/* ---- Pagination ---- */}
          {rows.length > 0 && data.pagination && (
            <Pagination
              page={page}
              perPage={perPage}
              total={data.pagination.total}
              hasPrev={data.pagination.hasPrev}
              hasNext={data.pagination.hasNext}
              onPageChange={onPageChange}
            />
          )}

          {/* ---- Legend ---- */}
          {showLegend && <PerformanceLegend buckets={legendBuckets} />}
        </>
      )}
    </div>
  );
};

export default ClassPerformanceHeatmap;